// Market Close-Up, the :x3 feature: the overview board, then every instrument in markets.closeup one at a time (last,
// today, five days, YTD and 52-week range in a row, then a Today chart beside the five-day one), every run (decided
// Oct 26; the Today chart Oct 27). A US index stays its index
// at every hour; outside the session its page adds the move since the close (pre-market, after hours or futures).
import { registerDisplay } from '../navigation.mjs';
import { escapeHtml } from '../utils/string.mjs';
import FeatureDisplay from './feature-display.mjs';
import { data } from './snapshot.mjs';
import { registerFeature } from './scheduler.mjs';
import marketState, {
	boardHtml, direction, labelFor, extraColumn, PLACES,
} from './market-watch.mjs';
import {
	asOf, price, percent, weekday, dayKey, timeZone,
} from './format.mjs';

const { markets } = window.RHL;
const PAGE_SECONDS = markets.closeupSeconds ?? 8;
const BOARD_SECONDS = markets.boardSeconds ?? 10;
const instruments = Object.fromEntries([...markets.board, ...(markets.macro ?? [])].map((i) => [i.key, i]));

const change = (from, to) => (from && to ? (to / from - 1) * 100 : null);
const pct = (value) => `<span class="${direction(value ?? 0)}">${percent(value, PLACES)}</span>`;

// The two charts, drawn alike on purpose (Oct 27: "as similar as possible"): the same size, grid, labels, line and
// end dot, and a dashed line at the price each one's number is measured from (five days ago for Five days,
// yesterday's close for Today), with the line green above it and red below.
const WIDTH = 336;
const HEIGHT = 134;
const PLOT = 110;
const hourFormat = new Intl.DateTimeFormat('en-US', { timeZone, hour: 'numeric' });
// "7A", "12P"
const hourLabel = (seconds) => hourFormat.format(seconds * 1000).replace(/ ?([AP])M$/, '$1');
const hour24 = new Intl.DateTimeFormat('en-US', { timeZone, hour: 'numeric', hourCycle: 'h23' });
const ptHour = (seconds) => Number(hour24.format(seconds * 1000));

// `tail` (Today after the close): the ETF or future since the close, drawn on in gold like the "since" number, over a
// faint gold band from the close; the end dot moves to its end
const GOLD = '#f0c860';
// `gap` (seconds): bars further apart than this aren't joined (Oct 29: the CME's daily break, Tokyo's lunch)
const lineChart = (points, {
	x, ref, ticks, tail = null, gap = Infinity,
}) => {
	if (!points?.length) return '';
	const values = points.map(([, c]) => c);
	const tailValues = tail?.length > 1 ? tail.map(([, c]) => c) : [];
	const span = [...values, ...tailValues, ref].filter((v) => v !== null && v !== undefined);
	const lo = Math.min(...span);
	const hi = Math.max(...span);
	const px = (point, i) => x(point, i) * WIDTH;
	const y = (c) => 6 + (1 - (c - lo) / (hi - lo || 1)) * (PLOT - 12);
	const join = (list, i) => (i && list[i][0] - list[i - 1][0] <= gap ? 'L' : 'M');
	const path = points.map((p, i) => `${join(points, i)}${px(p, i).toFixed(1)} ${y(p[1]).toFixed(1)}`).join(' ');
	const last = points.length - 1;
	const up = values[last] >= (ref ?? values[0]);
	const hasTail = tailValues.length > 1;
	const tailPath = hasTail ? tail.map((p, i) => `${join(tail, i)}${px(p).toFixed(1)} ${y(p[1]).toFixed(1)}`).join(' ') : '';
	const band = hasTail ? `<rect x="${px(tail[0]).toFixed(1)}" y="0" width="${(WIDTH - px(tail[0])).toFixed(1)}" height="${PLOT}" fill="${GOLD}" fill-opacity="0.08"/>` : '';
	const [dotX, dotY] = hasTail ? [px(tail.at(-1)), y(tailValues.at(-1))] : [px(points[last], last), y(values[last])];
	// a label needs room: none closer than 44 px to the one before it or hanging off the right edge
	let kept = -Infinity;
	const labelled = ticks.map((tick) => {
		const at = tick.at * WIDTH;
		const room = at - kept >= 44 && at <= WIDTH - 36;
		if (room) kept = at;
		return { ...tick, label: room ? tick.label : '' };
	});
	const grid = labelled.map(({ at, label }) => `<line x1="${(at * WIDTH).toFixed(1)}" y1="0" x2="${(at * WIDTH).toFixed(1)}" y2="${PLOT}" stroke="#1c2a8a" stroke-width="1"/>
		<text x="${(at * WIDTH + 4).toFixed(1)}" y="${HEIGHT - 4}" fill="#ffff00" font-family="Star4000 Small" font-size="20">${label}</text>`).join('');
	const dashed = ref === null || ref === undefined ? ''
		: `<line x1="0" y1="${y(ref).toFixed(1)}" x2="${WIDTH}" y2="${y(ref).toFixed(1)}" stroke="#b4bbe6" stroke-width="1.5" stroke-dasharray="6 5"/>`;
	return `<svg class="spark" viewBox="0 0 ${WIDTH} ${HEIGHT}" width="${WIDTH}" height="${HEIGHT}" aria-hidden="true">
		${band}${grid}<line x1="0" y1="${PLOT}" x2="${WIDTH}" y2="${PLOT}" stroke="#4c6bd9" stroke-width="1"/>${dashed}
		<path d="${path}" fill="none" stroke="${up ? '#3ddc5c' : '#ff5c3c'}" stroke-width="3" stroke-linejoin="round"/>
		${hasTail ? `<path d="${tailPath}" fill="none" stroke="${GOLD}" stroke-width="2.5" stroke-linejoin="round"/>` : ''}
		<circle cx="${dotX.toFixed(1)}" cy="${dotY.toFixed(1)}" r="4" fill="#ffff00"/>
	</svg>`;
};

// five days of closes, evenly spaced (the nights and the weekend fold away), a gridline at the start of each day
const fiveDayChart = (spark) => {
	if (!spark?.length) return '';
	const ticks = [];
	spark.forEach(([t], i) => {
		const key = dayKey(t * 1000);
		if (!ticks.length || ticks.at(-1).key !== key) ticks.push({ key, at: i / (spark.length - 1), label: weekday(t * 1000) });
	});
	return lineChart(spark, { x: (p, i) => i / (spark.length - 1), ref: spark[0][1], ticks });
};

// the latest session on its fixed axis (a US index 6:30 AM to 1 PM, and on past the close as far as its tail runs;
// futures and Bitcoin the last 24 hours). Oct 29 (QA at 9:20 PM): a tail longer than the session squeezed it into the
// left 45%, so the tail takes at most TAIL_SHARE of the width and past that runs on its own, shorter time scale. The
// session's gridlines are hourly, every two hours once a tail starts (one change, at the close, not as the tail
// grows); a compressed tail and a 24-hour axis space theirs the closest that leaves the labels room. Bars more than
// GAP_S apart aren't joined.
const TAIL_SHARE = 0.4;
const GAP_S = 40 * 60;
const STEPS = [1, 2, 3, 4, 6];
const hourTicks = (from, to, left, right, fixed = null) => {
	const perHour = ((right - left) * WIDTH * 3600) / (to - from || 1);
	const step = fixed ?? STEPS.find((s) => s * perHour >= 44) ?? 12;
	const ticks = [];
	for (let t = Math.ceil(from / 3600) * 3600; t < to; t += 3600) {
		if (ptHour(t) % step === 0) ticks.push({ at: left + ((t - from) / (to - from)) * (right - left), label: hourLabel(t) });
	}
	return ticks;
};
const todayChart = (today) => {
	if (!today?.points?.length) return '';
	const tail = today.tail?.length > 1 ? today.tail : null;
	const { from } = today;
	const close = today.to;
	const to = Math.max(close, tail?.at(-1)?.[0] ?? 0);
	const split = (close - from) / (to - from) >= 1 - TAIL_SHARE ? null : 1 - TAIL_SHARE;
	const at = (t) => {
		if (split === null) return (t - from) / (to - from);
		return t <= close ? split * ((t - from) / (close - from)) : split + (1 - split) * ((t - close) / (to - close));
	};
	let ticks;
	if (split !== null) ticks = [...hourTicks(from, close, 0, split, 2), ...hourTicks(close, to, split, 1)];
	else if (today.kind === 'us') ticks = hourTicks(from, to, 0, 1, tail ? 2 : 1);
	else ticks = hourTicks(from, to, 0, 1);
	return lineChart(today.points, {
		x: ([t]) => Math.min(1, Math.max(0, at(t))), ref: today.ref, ticks, tail, gap: GAP_S,
	});
};

// "Today" for a US index's session until midnight, then its weekday ("Mon + overnight" before Tuesday's open with the
// futures' tail, "Fri session" all weekend); futures and Bitcoin are "Last 24 hours"
const todayTitle = (today, now) => {
	// an index abroad: "Today" until its close, then its own weekday ("Wed session" here on a Tuesday evening)
	if (today.kind === 'session') {
		if (now.getTime() / 1000 <= today.to + 600) return 'Today';
		const day = new Date(today.to * 1000).toLocaleDateString('en-US', { timeZone: 'Asia/Tokyo', weekday: 'short' });
		return `${day} session`;
	}
	if (today.kind !== 'us') return 'Last 24 hours';
	if (dayKey(today.to * 1000) === dayKey(now)) return 'Today';
	const day = weekday(today.to * 1000);
	const name = `${day[0]}${day.slice(1).toLowerCase()}`;
	return today.tail?.length > 1 ? `${name} + overnight` : `${name} session`;
};

// pre-market, after hours or overnight: the move since the close, and where it comes from
const SINCE_NAMES = { Pre: 'Pre-mkt', After: 'After hrs', Fut: 'Futures' };
const sinceRow = (quote, now) => {
	const extra = extraColumn(marketState(now));
	if (!extra || extra.key !== 'since' || quote.since?.pct === undefined || quote.since?.pct === null) return null;
	const name = quote.since.from === 'future' && extra.head !== 'Fut' ? 'Futures' : SINCE_NAMES[extra.head];
	return [name, `<span class="since">${percent(quote.since.pct, 2)}</span>`];
};

const instrumentPage = (key, position, total, nextKey, now) => {
	const instrument = instruments[key];
	const quote = data('quotes')?.[key];
	const past = data('history')?.[key];
	if (!instrument || !quote) return null;
	// measured over exactly the span the chart draws, so the number and the line always agree
	const base = quote.spark?.[0]?.[1];
	const rows = [
		['Last', price(quote.last)],
		['Today', pct(quote.changePct)],
		sinceRow(quote, now),
		['5 days', pct(change(base, quote.last))],
		['YTD', pct(change(past?.prevYearClose, quote.last))],
		quote.low52 && quote.high52 ? ['52-wk', `${price(quote.low52)}-${price(quote.high52)}`] : null,
	].filter(Boolean);
	const nextQuote = nextKey ? data('quotes')?.[nextKey] : null;
	const next = nextKey ? ` &middot; next: ${escapeHtml(labelFor(instruments[nextKey] ?? {}, nextQuote) ?? nextKey)}` : '';
	const name = `${escapeHtml(labelFor(instrument, quote))} &middot; ${escapeHtml((quote.symbol ?? instrument.symbol).replace(/^\^/, '').replace(/=F$/, '').toUpperCase())}`;
	// Layout B (Oct 27): the numbers in one row, the two charts side by side under them; before the sidecar sends a
	// Today series, the five days sit in the left slot alone
	const today = quote.today?.points?.length ? quote.today : null;
	const charts = [
		today ? [todayTitle(today, now), todayChart(today)] : null,
		['Five days', fiveDayChart(quote.spark)],
	].filter(Boolean);
	return {
		seconds: PAGE_SECONDS,
		title: ['Market', 'Close-Up'],
		html: `<div class="closeup-b">
			<div class="top"><div class="hdr">${name}</div><div class="small dim">${position} of ${total}${next}</div></div>
			<div class="stats">${rows.map(([k, v]) => `<div><div class="k">${k}</div><div class="v">${v}</div></div>`).join('')}</div>
			<div class="charts">${charts.map(([head, svg]) => `<div><div class="hdr">${head}</div>${svg}</div>`).join('')}</div>
		</div>
		<div class="asof">${quote.stale ? 'Stale &middot; ' : ''}${asOf(quote.at, now)}</div>`,
	};
};

class MarketCloseup extends FeatureDisplay {
	constructor(navId, elemId) {
		super(navId, elemId, 'Market Close-Up');
	}

	// eslint-disable-next-line class-methods-use-this
	hasData() {
		return Object.keys(data('quotes') ?? {}).length > 0;
	}

	// eslint-disable-next-line class-methods-use-this
	buildPages(now) {
		const keys = markets.closeup.filter((key) => data('quotes')?.[key]);
		if (!keys.length) return [];
		const total = keys.length + 1;
		const pages = keys.map((key, i) => instrumentPage(key, i + 2, total, keys[i + 1], now)).filter(Boolean);
		return [{ seconds: BOARD_SECONDS, title: ['Markets', 'on the 3s'], html: boardHtml(now) }, ...pages];
	}
}

const display = new MarketCloseup(22, 'market-closeup');
registerDisplay(display);
registerFeature('market-closeup', display);
